// api/scanV2.ts — scan v2 calls (identify → confirm / reject / not-mine →
// label fallback). Everything goes through apiFetch, so a 402 opens the global
// top-up sheet and multipart bodies keep their boundary.
import { apiFetch, isApiError } from './config';
import type {
  LabelMeta,
  ScanConfirmResult,
  ScanContext,
  ScanIdentifyResult,
  ScanLabelResult,
  ScanRejectResult,
} from '../types/scanV2';

/** Most label photos the server accepts per attempt. */
export const MAX_LABEL_PARTS = 3;

/** Statuses whose scan body is a flow answer (refusal or retry), not a failure. */
const SCAN_BODY_STATUSES = new Set([409, 422]);

function isScanBody(payload: unknown): boolean {
  if (typeof payload !== 'object' || payload === null) return false;
  return 'scan_id' in payload || 'next' in payload;
}

/**
 * Runs a scan v2 call. A 409/422 that carries a scan body (`next`, `reason`,
 * `attempts_left`, `hint`, `candidates`) resolves as a result; anything else
 * is rethrown (402 CreditError, 400 brand_name_required ApiError, 503, …).
 */
async function scanCall<T>(path: string, init: RequestInit): Promise<T> {
  try {
    return await apiFetch<T>(path, init);
  } catch (error) {
    if (isApiError(error) && SCAN_BODY_STATUSES.has(error.status) && isScanBody(error.payload)) {
      return error.payload as T;
    }
    throw error;
  }
}

function scanPath(scanId: string, action: string): string {
  return `product-scan/${encodeURIComponent(scanId)}/${action}`;
}

function postJson<T>(path: string, body: unknown): Promise<T> {
  return scanCall<T>(path, { method: 'POST', body: JSON.stringify(body) });
}

function appendMeta(form: FormData, meta?: LabelMeta): void {
  if (!meta) return;
  for (const key of ['brand', 'name', 'variant'] as const) {
    const value = meta[key]?.trim();
    if (value) form.append(key, value);
  }
}

function cleanMeta(meta?: LabelMeta): LabelMeta {
  const out: LabelMeta = {};
  if (!meta) return out;
  for (const key of ['brand', 'name', 'variant'] as const) {
    const value = meta[key]?.trim();
    if (value) out[key] = value;
  }
  return out;
}

export function identifyBarcode(barcode: string, context: ScanContext): Promise<ScanIdentifyResult> {
  return postJson<ScanIdentifyResult>('product-scan/identify', { barcode, context });
}

export function identifyImage(file: Blob, context: ScanContext): Promise<ScanIdentifyResult> {
  const form = new FormData();
  form.append('image', file, 'product.jpg');
  form.append('context', context);
  return scanCall<ScanIdentifyResult>('product-scan/identify', { method: 'POST', body: form });
}

export function confirmCandidate(scanId: string, candidateId: string): Promise<ScanConfirmResult> {
  return postJson<ScanConfirmResult>(scanPath(scanId, 'confirm'), { candidate_id: candidateId });
}

export function rejectCandidates(scanId: string, candidateIds: string[]): Promise<ScanRejectResult> {
  return postJson<ScanRejectResult>(scanPath(scanId, 'reject'), { candidate_ids: candidateIds });
}

export function notMine(scanId: string): Promise<ScanRejectResult> {
  return scanCall<ScanRejectResult>(scanPath(scanId, 'not-mine'), { method: 'POST' });
}

export async function uploadLabel(
  scanId: string,
  parts: Blob[],
  meta?: LabelMeta,
): Promise<ScanLabelResult> {
  if (parts.length === 0 || parts.length > MAX_LABEL_PARTS) {
    throw new Error(`Send 1 to ${MAX_LABEL_PARTS} label photos`);
  }
  const form = new FormData();
  for (const [i, part] of parts.entries()) {
    form.append('ingredients_image', part, `label-${i + 1}.jpg`);
  }
  appendMeta(form, meta);
  return scanCall<ScanLabelResult>(scanPath(scanId, 'label'), { method: 'POST', body: form });
}

export function pasteLabelText(
  scanId: string,
  body: { text: string } & LabelMeta,
): Promise<ScanLabelResult> {
  return postJson<ScanLabelResult>(scanPath(scanId, 'label/text'), { text: body.text, ...cleanMeta(body) });
}

export function submitToTeam(scanId: string, meta: LabelMeta): Promise<ScanLabelResult> {
  return postJson<ScanLabelResult>(scanPath(scanId, 'label/submit'), cleanMeta(meta));
}
